import React from 'react'
import { parseISO, formatDistanceToNow, isToday, isThisWeek, isThisYear } from 'date-fns'
import { toZonedTime, format } from 'date-fns-tz'
import assert from 'assert'
import { useTranslation } from '../../providers/translation'
interface DateProps {
  /** The date string to format */
  date: string
  /** Additional class names for styling */
  className?: string
  /** Whether to show the date relative to the current time */
  relative?: boolean
  /** Whether to show the date in a detailed format */
  detailed?: boolean
}

export const stringToDate = (dateString: string) => {
  const rawDate = parseISOWithTimezoneFallback(dateString)
  const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone
  const zonedDateObj = toZonedTime(rawDate, timeZone)
  return zonedDateObj
}

/**
 * Date wrapper that formats a date string into a human-readable format.
 */
export const DateWrapper: React.FC<DateProps> = ({ date, className, relative, detailed }) => {
  const [currentTime, setCurrentTime] = React.useState(new Date())

  const [dateObj, timeZone] = React.useMemo(() => {
    const zonedDateObj = stringToDate(date)
    const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone
    return [zonedDateObj, timeZone]
  }, [date])

  assert(!detailed || !relative, 'Cannot have both detailed and relative date, feature TODO')

  React.useEffect(() => {
    if (!relative) return

    let howOften = 15 * 1000
    if (currentTime.getTime() - dateObj.getTime() > 60 * 60 * 1000) howOften = 5 * 60 * 1000
    if (currentTime.getTime() - dateObj.getTime() > 24 * 60 * 60 * 1000) howOften = 60 * 60 * 1000

    const interval = setInterval(() => {
      setCurrentTime(new Date())
    }, howOften)
    return () => clearInterval(interval)
  }, [dateObj, relative])

  const formattedDate = React.useMemo(() => {
    try {
      // Future
      if (currentTime.getTime() - dateObj.getTime() < 0) {
        if (isToday(dateObj)) return format(dateObj, 'h:mm a', { timeZone }) // Today
        if (isThisWeek(dateObj)) return format(dateObj, 'EEEE h:mm a', { timeZone }) // Day of the week
        if (isThisYear(dateObj)) return format(dateObj, 'MMM d, h:mm a', { timeZone }) // Month and day
        return format(dateObj, 'MMM d, yyyy, h:mm a', { timeZone }) // Month, day, and year
      }
      // Past
      if (relative) {
        if (currentTime.getTime() - dateObj.getTime() < 24 * 60 * 60 * 1000)
          return formatDistanceToNow(dateObj, { addSuffix: true }).replace('about ', '') // Less than a day ago
        if (isThisWeek(dateObj)) return format(dateObj, 'EEEE', { timeZone }) // Day of the week
        if (isThisYear(dateObj)) return format(dateObj, 'MMM d', { timeZone }) // Month and day
        return format(dateObj, 'MMM d, yyyy', { timeZone }) // Month, day, and year
      } else if (detailed) {
        if (isToday(dateObj)) return `Today, ${format(dateObj, 'h:mm a', { timeZone })}` // Today
        if (isThisWeek(dateObj)) return format(dateObj, 'EEEE h:mm a', { timeZone }) // Day of the week
        if (isThisYear(dateObj)) return format(dateObj, 'MMM d, h:mm a', { timeZone }) // Month and day
        return format(dateObj, 'MMM d, yyyy, h:mm a', { timeZone }) // Month
      } else {
        if (isToday(dateObj)) return format(dateObj, 'h:mm a', { timeZone }) // Today
        if (isThisWeek(dateObj)) return format(dateObj, 'EEEE', { timeZone }) // Day of the week
        if (isThisYear(dateObj)) return format(dateObj, 'MMM d', { timeZone }) // Month and day
        return format(dateObj, 'MMM d, yyyy', { timeZone }) // Month, day, and year
      }
    } catch {
      return 'invalid'
    }
  }, [dateObj, relative, currentTime])

  // the better way to do this is to load the date-fns locales, but this has to be done carefully to not bloat the bundle size
  const { tr } = useTranslation()
  const phrasesToTranslate = ['minutes ago', 'Today,']
  const translatedDate = phrasesToTranslate.reduce((acc, phrase) => {
    return acc.replace(phrase, tr(phrase))
  }, formattedDate)

  return <div className={`text-nowrap ${className}`}>{translatedDate}</div>
}

/**
 * Formats a date string for export, converting it to the local timezone and formatting it as 'MMM d, yyyy, h:mm a'.
 * @param date - The date string to format
 * @returns The formatted date string.
 * @example
 * formatDateForExport('2023-10-01T12:00:00Z') // 'Oct 1, 2023, 12:00 PM'
 */
export const formatDateForExport = (date: string) => {
  const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone
  const zonedDateObj = toZonedTime(date, timeZone)
  return format(zonedDateObj, 'MMM d, yyyy, h:mm a', { timeZone })
}

/**
 * Parses an ISO date string, and if it doesn't have a timezone, interprets it as UTC.
 */
export function parseISOWithTimezoneFallback(dateString: string) {
  const hasTimezone = /[+-]\d{2}(:\d{2})?$|Z$/.test(dateString)
  if (!hasTimezone) dateString = dateString + 'Z' // Add 'Z' to mark it as UTC
  return parseISO(dateString)
}
